import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import LottiePersons from "../LotiIcon/LottiePersons";
import LottieProduct from "../LotiIcon/LottieProduct";

/**
 * Continuous scrollytelling: hero + 5 narrative stages share one sticky
 * invoice card (marqeta.com/uk-style journey).
 *
 * Animation: rotateX, rotateY, translate-Y and scale are all interpolated
 * across scroll, then routed through useSpring for organic, bouncy motion
 * instead of a straight linear mapping. Every stage has its own pose.
 *
 *   Scroll zones (fractions of section height 550vh):
 *     hero    0      → 0.1818   (100vh)
 *     stage 0 0.1818 → 0.3455   (90vh)
 *     stage 1 0.3455 → 0.5091
 *     stage 2 0.5091 → 0.6727
 *     stage 3 0.6727 → 0.8364
 *     stage 4 0.8364 → 1.0
 */

const HERO_H = 100; // vh
const STAGE_H = 90; // vh each
const STAGES_N = 5;
const TOTAL_H = HERO_H + STAGE_H * STAGES_N; // 550
const HERO_END = HERO_H / TOTAL_H;
const STAGE_LEN = STAGE_H / TOTAL_H;
const STAGE_CENTERS = Array.from(
  { length: STAGES_N },
  (_, i) => HERO_END + STAGE_LEN * (i + 0.5)
);
const STAGE_BUCKETS = [
  HERO_END,
  ...Array.from({ length: STAGES_N }, (_, i) => HERO_END + STAGE_LEN * (i + 1)),
];
STAGE_BUCKETS[STAGE_BUCKETS.length - 1] = 1.0001;

// Horizontal landing positions for the floating invoice card. Negative =
// LEFT half of the viewport, positive = RIGHT half. Applied as translateX
// from the card's centred origin (the card itself is 340px wide).
const X_LEFT = -300;
const X_RIGHT = 300;

const STAGES = [
  {
    num: "01 / CREATE",
    pain: "Blank Excel. Logo that won't fit. Invoice number you forgot to update.",
    title: "Looks professional from invoice #1.",
    desc: "Pick a template, type the basics, and we handle the formatting, numbering, and totals — so you stop fighting your spreadsheet.",
    textSide: "left",
    rotateX: 18,
    rotateY: -22,
    xStart: X_RIGHT,
    xEnd: X_RIGHT,
    y: 20,
    scale: 0.88,
    glow: "rgba(24,80,255,0.14)",
  },
  {
    num: "02 / TAX",
    pain: "Calculator in one hand, phone in the other. Is this CGST + SGST, or IGST?",
    title: "Tax maths, handled.",
    desc: "Local sale or interstate, we split it correctly every time. Discounts, rounding, HSN codes — all sorted. You'll never reach for a calculator again.",
    textSide: "right",
    rotateX: -10,
    rotateY: 18,
    xStart: X_LEFT,
    xEnd: X_LEFT,
    y: -10,
    scale: 0.96,
    glow: "rgba(24,80,255,0.18)",
  },
  {
    num: "03 / SEND",
    pain: "PDF that won't open. Email bouncing. Client asking you to send it again.",
    title: "Send it where they actually read.",
    desc: "WhatsApp the casual clients, email a PDF to the formal ones, share a Drive link for bulk buyers. Same invoice, three channels, one tap each.",
    textSide: "left",
    rotateX: 4,
    rotateY: -6,
    xStart: X_LEFT,
    xEnd: X_RIGHT,
    y: -4,
    scale: 1.04,
    glow: "rgba(30,120,255,0.22)",
  },
  {
    num: "04 / PAID",
    pain: "Calling clients on Sunday. Following up, and  Following up....",
    title: "Payment in two taps, not two weeks.",
    desc: "Every invoice carries a payment link. Customers pay from their phone, status flips to Paid in your dashboard, and the chasing stops.",
    textSide: "right",
    rotateX: 0,
    rotateY: 0,
    xStart: X_LEFT,
    xEnd: X_LEFT,
    y: -24,
    scale: 1.02,
    glow: "rgba(22,197,94,0.18)",
  },
  {
    num: "05 / FILED",
    pain: "Midnight before the GSTR deadline. Spreadsheets everywhere. CA on speed dial.",
    title: "Month-end without the headache.",
    desc: "GSTR-1 and GSTR-3B file in one click via a government-approved API, and your data backs up to your own Google Drive. No CA panic, no late fees.",
    textSide: "left",
    rotateX: -12,
    rotateY: 10,
    xStart: X_LEFT,
    xEnd: X_RIGHT,
    y: -6,
    scale: 1.08,
    glow: "rgba(22,197,94,0.18)",
  },
];

// Hero pose: card lands on the RIGHT so it lines up with Stage 0's
// right-side text-on-the-left layout without a jump on first scroll.
const HERO_POSE = { rotateX: 0, rotateY: -6, x: X_RIGHT, y: 0, scale: 1 };

// Stage-centre anchors drive rotation/scale/y so each stage's pose lands at
// its midpoint. X uses its own anchor list with start AND end points so
// stages 03 (COMPOSING) and 05 (FILING) can interpolate left → right
// during the stage instead of snapping.
const ROT_ANCHORS = [0, HERO_END, ...STAGE_CENTERS];
const ROT_X = [HERO_POSE.rotateX, HERO_POSE.rotateX, ...STAGES.map((s) => s.rotateX)];
const ROT_Y = [HERO_POSE.rotateY, HERO_POSE.rotateY, ...STAGES.map((s) => s.rotateY)];
const TRANS_Y = [HERO_POSE.y, HERO_POSE.y, ...STAGES.map((s) => s.y)];
const SCALE_V = [HERO_POSE.scale, HERO_POSE.scale, ...STAGES.map((s) => s.scale)];

// Epsilon defines how much scroll progress the card spends swinging between
// adjacent stages whose x is opposite. Larger value = longer, more visible
// drift; smaller value = a snappy hand-off that the spring has to absorb.
const X_EPS = 0.015;
const X_ANCHORS = [
  0,
  HERO_END,
  ...STAGES.flatMap((_, i) => [
    HERO_END + STAGE_LEN * i + X_EPS,
    HERO_END + STAGE_LEN * (i + 1) - X_EPS,
  ]),
];
const X_VALS = [
  HERO_POSE.x,
  HERO_POSE.x,
  ...STAGES.flatMap((s) => [s.xStart, s.xEnd]),
];

// Lower stiffness + heavier mass = the card visibly lags behind scroll, so
// each stage transition reads as a deliberate beat rather than a snap.
const SPRING_CFG = { stiffness: 120, damping: 25, mass: 1.1 };

function FontAndBaseStyles() {
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;600&family=Josefin+Sans:wght@400;600;700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .scrolly-root {
          background:
            radial-gradient(60rem 40rem at 15% 10%, rgba(37,99,235,0.25), transparent 60%),
            radial-gradient(55rem 36rem at 85% 70%, rgba(79,70,229,0.25), transparent 60%),
            linear-gradient(to bottom right, #111827, #000000 45%, #1e3a8a);
          color: #e8eaf0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .scrolly-grid-bg {
          background-image:
            linear-gradient(rgba(30,90,255,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(30,90,255,0.07) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .scrolly-mono { font-family: 'JetBrains Mono', monospace; }
        .scrolly-serif { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .scrolly-stage-num {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: #1e5aff;
          letter-spacing: 0.14em;
          font-weight: 600;
        }
        .scrolly-stage-title {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 44px;
          line-height: 1.05;
          color: #f3f5fb;
          letter-spacing: -0.025em;
          font-weight: 700;
        }
        .scrolly-stage-desc {
          font-size: 15px;
          color: #9ba8c8;
          line-height: 1.6;
          max-width: 420px;
        }
        .scrolly-stage-pain {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 13px;
          color: #6b7785;
          font-style: italic;
          line-height: 1.55;
          margin-top: 10px;
          max-width: 380px;
        }
        .scrolly-stage-pain::before {
          content: "✗";
          color: #ef4444;
          font-weight: 700;
          font-style: normal;
          margin-right: 8px;
          letter-spacing: 0;
        }
        .scrolly-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(34,197,94,0.12);
          border: 1px solid rgba(34,197,94,0.3);
          border-radius: 999px;
          font-size: 13px;
          color: #86efac;
        }
        .scrolly-hero-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #4ade80;
          box-shadow: 0 0 10px #4ade80;
          animation: scrolly-pulse 2s infinite;
        }
        @keyframes scrolly-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .scrolly-hero-title {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: clamp(44px, 5.6vw, 72px);
          line-height: 1.02;
          letter-spacing: -0.035em;
          color: #f3f5fb;
          margin-top: 22px;
          font-weight: 800;
        }
        .scrolly-hero-title em {
          font-style: normal;
          background: linear-gradient(90deg, #60a5fa, #818cf8);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .scrolly-hero-kicker {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #60a5fa;
          margin-top: 14px;
        }
        .scrolly-hero-lede {
          font-size: 17px;
          color: #9ba8c8;
          line-height: 1.6;
          margin-top: 18px;
          max-width: 480px;
        }
        .scrolly-hero-ctas {
          display: flex;
          gap: 12px;
          margin-top: 28px;
          flex-wrap: wrap;
        }
        .scrolly-hero-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 24px;
          border-radius: 999px;
          background: linear-gradient(90deg, #2563eb, #4f46e5);
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 18px 40px rgba(37,99,235,0.35);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: none;
          cursor: pointer;
        }
        .scrolly-hero-btn-primary:hover { transform: translateY(-1px); }
        .scrolly-hero-btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 24px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.05);
          color: #e8eaf0;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          backdrop-filter: blur(8px);
          transition: background 0.2s ease;
        }
        .scrolly-hero-btn-ghost:hover { background: rgba(255,255,255,0.1); }
        .scrolly-hero-scrollhint {
          margin-top: 40px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: #64748b;
          letter-spacing: 0.16em;
        }
        .scrolly-hero-scrollhint svg { animation: scrolly-bob 1.8s ease-in-out infinite; }
        @keyframes scrolly-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
        .scrolly-invoice-card {
          background: #ffffff;
          border-radius: 14px;
          overflow: hidden;
          position: relative;
          transform-style: preserve-3d;
          display: flex;
          flex-direction: column;
          box-shadow:
            0 0 0 1px rgba(30,90,255,0.2),
            0 24px 80px rgba(0,0,0,0.6),
            0 0 60px rgba(30,90,255,0.08);
          transition: box-shadow 0.5s ease;
        }
        .scrolly-invoice-card.stage-3 {
          box-shadow:
            0 0 0 1px rgba(30,90,255,0.4),
            0 24px 80px rgba(0,0,0,0.6),
            0 0 80px rgba(30,90,255,0.18);
        }
        .scrolly-invoice-card.stage-5 {
          box-shadow:
            0 0 0 1px rgba(22,197,94,0.5),
            0 24px 80px rgba(0,0,0,0.6),
            0 0 80px rgba(22,197,94,0.15);
        }
        .scrolly-inv-header {
          background: #0f172a;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .scrolly-inv-logo {
          font-family: 'Josefin Sans', 'Segoe UI', sans-serif;
          font-size: 18px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: 0.01em;
          line-height: 1;
          text-transform: none;
        }
        .scrolly-inv-logo span { color: #1e5aff; font-weight: 700; }
        .scrolly-inv-badge {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          color: #94a3b8;
          letter-spacing: 0.1em;
        }
        .scrolly-inv-body {
          padding: 18px;
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
        }
        .scrolly-inv-meta {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .scrolly-inv-label {
          font-size: 9px;
          color: #94a3b8;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.08em;
        }
        .scrolly-inv-num { text-align: right; }
        .scrolly-inv-divider { height: 1px; background: #f1f5f9; }
        .scrolly-inv-table-head {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 4px;
          padding: 4px 0;
        }
        .scrolly-inv-table-head span {
          font-size: 9px;
          color: #94a3b8;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.08em;
          font-weight: 600;
        }
        .scrolly-inv-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 4px;
          padding: 6px 0;
          border-bottom: 1px solid #f8fafc;
          align-items: center;
        }
        .scrolly-inv-total {
          margin-top: auto;
          background: #f8fafc;
          border-radius: 10px;
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .scrolly-inv-total-label { font-size: 11px; color: #64748b; letter-spacing: 0.06em; }
        .scrolly-placeholder {
          height: 10px;
          border-radius: 4px;
          background: #e2e8f0;
        }
        .scrolly-placeholder.blue { background: rgba(30,90,255,0.18); }
        .scrolly-placeholder.dark { background: #cbd5e1; }
        .scrolly-placeholder.h20 { height: 20px; }
        .scrolly-stamp {
          border: 3px solid #16c55e;
          border-radius: 10px;
          padding: 12px 22px;
          text-align: center;
        }
        .scrolly-stamp-text {
          font-family: 'JetBrains Mono', monospace;
          font-size: 16px;
          font-weight: 600;
          color: #16c55e;
          letter-spacing: 0.14em;
          display: block;
        }
        .scrolly-stamp-sub {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          color: #16c55e;
          letter-spacing: 0.14em;
          opacity: 0.75;
        }
      `}</style>
    </>
  );
}

function InvoiceHeader() {
  return (
    <div className="scrolly-inv-header">
      <div className="scrolly-inv-logo">
        Logi<span>Slip</span>
      </div>
      <div className="scrolly-inv-badge">TAX INVOICE</div>
    </div>
  );
}

const fadeVariants = {
  enter: { opacity: 0, y: 8 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

/* All stage bodies use masked placeholder bars only. Actual data is never
   shown — only the LogiSlip wordmark and TAX INVOICE label in the header. */

function Stage0Body() {
  return (
    <motion.div
      key="s0"
      variants={fadeVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.35 }}
      className="scrolly-inv-body"
    >
      <div className="scrolly-inv-meta">
        <div style={{ flex: 1 }}>
          <span className="scrolly-inv-label">BILL FROM</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <div className="scrolly-placeholder" style={{ width: "70%" }} />
            <div className="scrolly-placeholder" style={{ width: "50%" }} />
          </div>
        </div>
        <div className="scrolly-inv-num">
          <span className="scrolly-inv-label">INV NO.</span>
          <div className="scrolly-placeholder" style={{ width: 68, marginTop: 8, marginLeft: "auto" }} />
        </div>
      </div>
      <div className="scrolly-inv-divider" style={{ marginTop: 8 }} />
      <div className="scrolly-inv-table-head">
        <span>ITEM</span>
        <span>QTY</span>
        <span>AMT</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="scrolly-placeholder h20" style={{ width: "85%" }} />
        <div className="scrolly-placeholder h20" style={{ width: "80%" }} />
        <div className="scrolly-placeholder h20" style={{ width: "65%" }} />
      </div>
      <div style={{ marginTop: "auto" }}>
        <div
          className="scrolly-placeholder h20 blue"
          style={{ width: "40%", marginLeft: "auto" }}
        />
      </div>
      <div
        className="scrolly-mono"
        style={{
          fontSize: 10,
          color: "#cbd5e1",
          textAlign: "center",
          letterSpacing: "0.1em",
          marginTop: 4,
        }}
      >
        awaiting data...
      </div>
    </motion.div>
  );
}

function MaskedRow({ delay = 0, widths = [75, 40, 60] }) {
  const [w1, w2, w3] = widths;
  return (
    <motion.div
      className="scrolly-inv-row"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
    >
      <div
        className="scrolly-placeholder"
        style={{ width: `${w1}%`, justifySelf: "start" }}
      />
      <div
        className="scrolly-placeholder"
        style={{ width: `${w2}%`, justifySelf: "start" }}
      />
      <div
        className="scrolly-placeholder dark"
        style={{ width: `${w3}%`, justifySelf: "end" }}
      />
    </motion.div>
  );
}

function Stage1Body() {
  return (
    <motion.div
      key="s1"
      variants={fadeVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.35 }}
      className="scrolly-inv-body"
    >
      <div className="scrolly-inv-meta">
        <div style={{ flex: 1 }}>
          <span className="scrolly-inv-label">BILL FROM</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <div className="scrolly-placeholder dark" style={{ width: "78%" }} />
            <div className="scrolly-placeholder" style={{ width: "60%" }} />
            <div className="scrolly-placeholder" style={{ width: "45%" }} />
          </div>
        </div>
        <div className="scrolly-inv-num">
          <span className="scrolly-inv-label">INV NO.</span>
          <div
            className="scrolly-placeholder blue"
            style={{ width: 80, marginTop: 8, marginLeft: "auto" }}
          />
        </div>
      </div>
      <div className="scrolly-inv-divider" />
      <div className="scrolly-inv-table-head">
        <span>ITEM</span>
        <span>QTY</span>
        <span>AMOUNT</span>
      </div>
      <MaskedRow delay={0.05} widths={[80, 35, 65]} />
      <MaskedRow delay={0.18} widths={[72, 40, 58]} />
      <motion.div
        className="scrolly-placeholder h20"
        style={{ width: "78%" }}
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      />
      <div style={{ marginTop: "auto" }}>
        <div
          className="scrolly-placeholder h20 blue"
          style={{ width: "40%", marginLeft: "auto" }}
        />
      </div>
      <div
        className="scrolly-mono"
        style={{
          fontSize: 10,
          color: "#1e5aff",
          textAlign: "center",
          letterSpacing: "0.1em",
          marginTop: 4,
        }}
      >
        calculating tax...
      </div>
    </motion.div>
  );
}

function FullInvoiceBody({ keyName = "full" }) {
  return (
    <motion.div
      key={keyName}
      variants={fadeVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.35 }}
      className="scrolly-inv-body"
    >
      <div className="scrolly-inv-meta">
        <div style={{ flex: 1 }}>
          <span className="scrolly-inv-label">BILL FROM</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <div className="scrolly-placeholder dark" style={{ width: "80%" }} />
            <div className="scrolly-placeholder" style={{ width: "60%" }} />
            <div className="scrolly-placeholder" style={{ width: "50%" }} />
          </div>
        </div>
        <div className="scrolly-inv-num">
          <span className="scrolly-inv-label">INV NO.</span>
          <div
            className="scrolly-placeholder blue"
            style={{ width: 82, marginTop: 8, marginLeft: "auto" }}
          />
          <div
            className="scrolly-placeholder"
            style={{ width: 60, height: 8, marginTop: 6, marginLeft: "auto" }}
          />
        </div>
      </div>
      <div className="scrolly-inv-divider" />
      <div className="scrolly-inv-table-head">
        <span>ITEM</span>
        <span>QTY</span>
        <span>AMOUNT</span>
      </div>
      <MaskedRow widths={[82, 38, 62]} />
      <MaskedRow widths={[72, 44, 58]} />
      <MaskedRow widths={[78, 36, 65]} />
      <div className="scrolly-inv-divider" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="scrolly-placeholder" style={{ width: 60, height: 8 }} />
        <div className="scrolly-placeholder" style={{ width: 70, height: 8 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="scrolly-placeholder" style={{ width: 50, height: 8 }} />
        <div className="scrolly-placeholder" style={{ width: 62, height: 8 }} />
      </div>
      <div className="scrolly-inv-total">
        <span className="scrolly-inv-total-label">TOTAL DUE</span>
        <div className="scrolly-placeholder blue" style={{ width: 110, height: 18 }} />
      </div>
    </motion.div>
  );
}

function EnvelopeOverlay() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(6,9,15,0.9)",
        borderRadius: 14,
        pointerEvents: "none",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <svg
          width="72"
          height="56"
          viewBox="0 0 64 48"
          fill="none"
          style={{ display: "block", margin: "0 auto 14px" }}
        >
          <rect
            x="2"
            y="8"
            width="60"
            height="38"
            rx="4"
            fill="#1e2d5a"
            stroke="#1e5aff"
            strokeWidth="1.5"
          />
          <path
            d="M2 12 L32 30 L62 12"
            stroke="#1e5aff"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="32" cy="30" r="12" fill="#1e5aff" opacity="0.15" />
          <path
            d="M26 30 L32 4 L38 30"
            stroke="#60a5fa"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div style={{ fontSize: 14, color: "#e8eaf0", fontWeight: 600 }}>
          Invoice sent
        </div>
        <div
          className="scrolly-mono"
          style={{ fontSize: 11, color: "#4a5580", marginTop: 4 }}
        >
          → client@company.in
        </div>
      </div>
    </motion.div>
  );
}

function GstStamp() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
      animate={{ opacity: 1, scale: 1, rotate: -8 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div className="scrolly-stamp">
        <span className="scrolly-stamp-text">GST FILED</span>
        <span className="scrolly-stamp-sub">COMPLIANCE VERIFIED</span>
      </div>
    </motion.div>
  );
}

function PaidStamp() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
      animate={{ opacity: 1, scale: 1, rotate: -8 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div className="scrolly-stamp">
        <span className="scrolly-stamp-text">PAID</span>
        <span className="scrolly-stamp-sub">VERIFIED</span>
      </div>
    </motion.div>
  );
}

function HeroBlock({ onStartFree }) {
  return (
    <div>
      <span className="scrolly-hero-eyebrow">
        <span className="scrolly-hero-eyebrow-dot" />
        One-Click GST Filing Now Available
      </span>
      <h1 className="scrolly-hero-title">
        Invoice <em>Reimagined</em>
      </h1>
      <div className="scrolly-hero-kicker">The next era of invoicing</div>
      <p className="scrolly-hero-lede">
        Take invoicing to the next level. Automate tax workflow. Experience
        unmatched speed, compliance, and reliability.
      </p>
      <div className="scrolly-hero-ctas">
        <button
          type="button"
          onClick={onStartFree}
          className="scrolly-hero-btn-primary"
        >
          <svg
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          Start Free
        </button>
      </div>
    </div>
  );
}

export default function ScrollytellingInvoice({ onStartFree }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Raw scroll-linked transforms (straight-line interpolation).
  const rawRotateX = useTransform(scrollYProgress, ROT_ANCHORS, ROT_X);
  const rawRotateY = useTransform(scrollYProgress, ROT_ANCHORS, ROT_Y);
  const rawX = useTransform(scrollYProgress, X_ANCHORS, X_VALS);
  const rawY = useTransform(scrollYProgress, ROT_ANCHORS, TRANS_Y);
  const rawScale = useTransform(scrollYProgress, ROT_ANCHORS, SCALE_V);

  // Route each axis through a spring so motion feels organic, not linear.
  const rotateX = useSpring(rawRotateX, SPRING_CFG);
  const rotateY = useSpring(rawRotateY, SPRING_CFG);
  const xSpring = useSpring(rawX, SPRING_CFG);
  const ySpring = useSpring(rawY, SPRING_CFG);
  const scaleSpring = useSpring(rawScale, SPRING_CFG);

  const [view, setView] = useState("hero");

  useEffect(() => {
    const resolve = (v) => {
      if (v < HERO_END) return "hero";
      for (let i = 0; i < STAGES_N; i++) {
        if (v >= STAGE_BUCKETS[i] && v < STAGE_BUCKETS[i + 1]) return i;
      }
      return STAGES_N - 1;
    };
    const unsub = scrollYProgress.onChange((v) => {
      const nv = resolve(v);
      setView((prev) => (prev === nv ? prev : nv));
    });
    setView(resolve(scrollYProgress.get()));
    return () => unsub();
  }, [scrollYProgress]);

  const isHero = view === "hero";
  const activeGlow = isHero
    ? "rgba(30,90,255,0.18)"
    : STAGES[view].glow;
  // SEND (view 2) keeps the boosted-blue glow; PAID (3) and FILED (4) both
  // wear the green success halo.
  const cardStateClass =
    view === 2 ? "stage-3" : view === 3 || view === 4 ? "stage-5" : "";

  const renderBody = () => {
    if (isHero) return <FullInvoiceBody keyName="hero" />;
    if (view === 0) return <Stage0Body />;
    if (view === 1) return <Stage1Body />;
    return <FullInvoiceBody keyName="full" />;
  };

  return (
    <>
      <FontAndBaseStyles />
      <section
        ref={sectionRef}
        id="features"
        className="scrolly-root relative"
        style={{ height: `${TOTAL_H}vh` }}
      >
        <div className="scrolly-grid-bg absolute inset-0 pointer-events-none" />

        {/* Sticky invoice overlay: full-section absolute, sticky child centres
            inside the viewport. The card translates left ↔ right via xSpring
            while text in the content stack flows past underneath. */}
        <div
          className="hidden md:block absolute inset-0 pointer-events-none"
          style={{ zIndex: 10 }}
        >
          <div
            className="sticky top-0 w-full"
            style={{ height: "100vh" }}
          >
            <motion.div
              style={{
                x: xSpring,
                position: "absolute",
                top: "50%",
                left: "50%",
                marginLeft: -170,
                marginTop: -230,
                width: 340,
                height: 460,
                willChange: "transform",
              }}
            >
              <motion.div
                aria-hidden
                animate={{
                  background: `radial-gradient(circle, ${activeGlow} 0%, transparent 70%)`,
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{
                  position: "absolute",
                  width: 620,
                  height: 620,
                  borderRadius: "50%",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />

              {/* Hero-only lotties orbit the card and travel with its x */}
              <AnimatePresence>
                {isHero && (
                  <motion.div
                    key="hero-lotties"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="pointer-events-none absolute"
                    style={{
                      top: "50%",
                      left: "50%",
                      width: 460,
                      height: 540,
                      marginLeft: -230,
                      marginTop: -270,
                      zIndex: 4,
                    }}
                  >
                    <motion.div
                      className="absolute"
                      style={{ top: -20, right: -24 }}
                      animate={{ y: [-8, 6, -8], x: [2, -2, 2] }}
                      transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                    >
                      <LottiePersons className="w-[120px] h-[120px] opacity-90" loop />
                    </motion.div>
                    <motion.div
                      className="absolute"
                      style={{ top: "38%", left: -60 }}
                      animate={{ y: [6, -8, 6], x: [-3, 2, -3] }}
                      transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
                    >
                      <LottieProduct className="w-[120px] h-[120px] opacity-90" loop />
                    </motion.div>
                    <motion.div
                      className="absolute"
                      style={{ bottom: -18, left: -10 }}
                      animate={{ y: [-4, 6, -4], x: [1, -2, 1] }}
                      transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
                    >
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Rotation / scale / y wrapper — all the 3D pose, no x */}
              <motion.div
                style={{
                  rotateX,
                  rotateY,
                  y: ySpring,
                  scale: scaleSpring,
                  transformPerspective: 1400,
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  zIndex: 5,
                  willChange: "transform",
                }}
              >
                {/* Idle float keeps the card alive when scroll is paused */}
                <motion.div
                  animate={{ rotate: [0, 0.6, -0.4, 0], y: [0, -3, 2, 0] }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{ width: "100%", height: "100%" }}
                >
                  <div
                    className={`scrolly-invoice-card ${cardStateClass}`}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <InvoiceHeader />
                    <AnimatePresence mode="wait">{renderBody()}</AnimatePresence>
                    <AnimatePresence>
                      {view === 2 && <EnvelopeOverlay key="env" />}
                      {view === 3 && <PaidStamp key="paid" />}
                      {view === 4 && <GstStamp key="stamp" />}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Stage indicator dashes pinned to bottom of viewport */}
            <div
              style={{
                position: "absolute",
                bottom: 40,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
                alignItems: "center",
              }}
            >
              <span
                style={{
                  height: 4,
                  width: isHero ? 24 : 8,
                  borderRadius: 3,
                  background: isHero ? "#60a5fa" : "#1a2040",
                  border: "1px solid #2a3060",
                  transition: "all 0.35s ease",
                }}
              />
              {STAGES.map((_, i) => (
                <span
                  key={i}
                  style={{
                    height: 4,
                    width: view === i ? 24 : 8,
                    borderRadius: 3,
                    background: view === i ? "#1e5aff" : "#1a2040",
                    border: "1px solid #2a3060",
                    transition: "all 0.35s ease",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content stack — full-width container, each stage block aligns to
            its textSide (left or right). The sticky card overlays this
            column from above and slides between sides per stage. */}
        <div
          className="relative max-w-7xl mx-auto px-6"
          style={{ zIndex: 1 }}
        >
          <div
            className="flex items-center"
            style={{ height: `${HERO_H}vh` }}
          >
            <div className="w-full md:w-1/2 md:pr-6">
              <HeroBlock onStartFree={onStartFree} />
            </div>
          </div>

          {STAGES.map((s, i) => (
            <div
              key={i}
              className={`flex items-center ${
                s.textSide === "right" ? "md:justify-end" : "md:justify-start"
              }`}
              style={{ height: `${STAGE_H}vh` }}
            >
              <div
                className={`w-full md:w-1/2 ${
                  s.textSide === "right" ? "md:pl-6" : "md:pr-6"
                }`}
              >
                <motion.div
                  animate={{
                    opacity: view === i ? 1 : 0.25,
                    x: view === i ? 0 : -4,
                  }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  style={{
                    borderLeft:
                      view === i
                        ? "2px solid #1e5aff"
                        : "2px solid transparent",
                    background:
                      view === i ? "rgba(30,90,255,0.05)" : "transparent",
                    borderRadius: 6,
                    padding: "16px 20px",
                  }}
                >
                  <div className="scrolly-stage-num">{s.num}</div>
                  <p className="scrolly-stage-pain">{s.pain}</p>
                  <h3
                    className="scrolly-stage-title"
                    style={{ marginTop: 12 }}
                  >
                    {s.title}
                  </h3>
                  <p className="scrolly-stage-desc" style={{ marginTop: 14 }}>
                    {s.desc}
                  </p>
                </motion.div>
              </div>
            </div>
          ))}
        </div>

        <motion.div
          style={{
            position: "sticky",
            bottom: 0,
            top: "auto",
            height: 2,
            scaleX: scrollYProgress,
            transformOrigin: "0 0",
            background: "linear-gradient(90deg, #1e5aff, #60a5fa)",
            zIndex: 20,
            marginTop: "-2px",
          }}
        />
      </section>
    </>
  );
}
